/**
 * BulkEntityAIEnhance — the shared bulk AI enhancement dialog.
 *
 * One implementation of the review→apply flow every listing's "AI Enhance"
 * button opens: config chosen up front (confidence threshold + write mode), a
 * bounded-concurrency Run with a Stop, a per-cell grid of what the model
 * proposed, and an Apply that writes only the ticked cells.
 *
 * Extracted verbatim from BulkWinderAIEnhance, which was itself a copy of
 * BulkCalibreAIEnhance. Everything entity-specific now arrives through a
 * {@link BulkEnhanceDescriptor}: which callable to run, which field registry to
 * read, and the handful of nouns that appear in the copy. Adding a third
 * listing is a descriptor, not another 800 lines.
 *
 * CONFIDENCE IS PER CELL here. That is the shape `enrichWinder` and
 * `enrichElectronicModule` return (`{ field: { value, confidence } }`), so the
 * threshold gates each cell and the dot beside a row's name is a summary — the
 * best confidence anything in that row achieved. `enrichCalibre` returns ONE
 * confidence per row and writes nested `data.*` paths, which is why
 * BulkCalibreAIEnhance is still its own component rather than a third
 * descriptor; folding it in needs a row-confidence mode and dotted-path
 * support, and is a job of its own.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../services/firebase';
import {
  cellAgreement,
  defaultChecked,
  meetsThreshold,
  type EnrichThreshold,
  type EnrichWriteMode,
} from '../../services/bulkEnrichFields';
import { Button } from './Button';
import { ColumnSelectAllCheckbox } from './ColumnSelectAllCheckbox';
import { Modal } from './Modal';

// Bounded parallelism — keeps us within the enrichment Cloud Function /
// Anthropic rate limits. Same value the per-entity copies used.
const CONCURRENCY = 4;

/** The minimum an entity has to expose to be enhanced in bulk. */
export interface BulkEnhanceEntry {
  id: string;
}

/**
 * Everything one listing has to say to reuse the dialog.
 *
 * The field accessors are the same pure registry functions the entity's
 * single-item review already uses (`winderEnrichFields`, `moduleEnrichFields`),
 * so a bulk write is indistinguishable from an editor save.
 */
export interface BulkEnhanceDescriptor<TEntry extends BulkEnhanceEntry, TResult> {
  /** Firestore collection the apply pass writes to. */
  collection: string;
  /**
   * Distinguishes this dialog's radio groups from any other mounted at the
   * same time. Two dialogs sharing a `name` would share a selection.
   */
  formName: string;
  /** Run the enrichment callable for one entry. */
  enrich: (entry: TEntry) => Promise<TResult>;

  // ---- field registry -------------------------------------------------------
  /** Column order. A key the run never proposes simply doesn't appear. */
  fieldOrder: string[];
  /** Keys this result actually proposes a value for. */
  suggestedKeys: (result: TResult, entry: TEntry) => string[];
  fieldLabel: (key: string) => string;
  /** The entry's stored value as a display string; '' means "no value". */
  getExisting: (entry: TEntry, key: string) => string;
  getSuggestion: (result: TResult, key: string) => unknown;
  getConfidence: (result: TResult, key: string) => string;
  /** Normalise a raw suggestion to the type the field stores. */
  coerceValue: (key: string, value: unknown) => unknown;
  /**
   * Render a value for display AND for the agreement comparison.
   *
   * One formatter on both sides is load-bearing: the apply pass string-compares
   * a proposal against `getExisting` to decide whether it changes anything, so
   * two formatters that disagree make an agreeing value read as `differs` and
   * rewrite a document the model merely confirmed.
   */
  formatValue: (value: unknown) => string;
  /** Firestore patch for the accepted keys. Unusable values are dropped. */
  buildPatch: (entry: TEntry, keys: string[], result: TResult) => Record<string, unknown>;
  /**
   * Keys that default to checked whenever they meet the threshold, ignoring the
   * write mode and any existing value (typically the AI description).
   */
  alwaysApplyKeys: Set<string>;
  /**
   * Keys that NEVER default to checked, whatever the mode — a proposal too
   * consequential to accept in bulk without looking at it (a rename). Still
   * shown, still tickable by hand.
   */
  neverDefaultKeys?: Set<string>;

  // ---- copy -----------------------------------------------------------------
  /** Row label, and the optional second line under it. */
  rowLabel: (entry: TEntry) => string;
  rowSubLabel?: (entry: TEntry) => string;
  copy: {
    title: (count: number) => string;
    /** Header of the sticky first column. */
    rowHeader: string;
    runButton: (count: number) => string;
    runComplete: (ok: number) => string;
    applied: (fields: number, rows: number) => string;
    appliedWithFailures: (rows: number, failed: number) => string;
    failures: (count: number) => string;
    /** Entity-specific caveats under the config block. */
    configNote: ReactNode;
    /** Entity-specific footnote under the grid. */
    legendNote: ReactNode;
  };
}

type RunStatus = 'queued' | 'running' | 'done' | 'failed';
type ApplyStatus = 'idle' | 'applying' | 'applied' | 'failed' | 'skipped';

interface EntryRun<TEntry, TResult> {
  entry: TEntry;
  status: RunStatus;
  error?: string;
  /** Per-field result map from the callable. Empty until the run completes. */
  result: TResult;
}

// checked[entryId][fieldKey] = whether that cell is selected for write-back.
type CheckedMap = Record<string, Record<string, boolean>>;

export interface BulkEntityAIEnhanceProps<TEntry extends BulkEnhanceEntry, TResult> {
  isOpen: boolean;
  onClose: () => void;
  /** The currently-selected entries to enhance. */
  entries: TEntry[];
  /** Called after a write-back commits, so the host can refresh + clear selection. */
  onApplied: () => void;
  descriptor: BulkEnhanceDescriptor<TEntry, TResult>;
}

export function BulkEntityAIEnhance<TEntry extends BulkEnhanceEntry, TResult>({
  isOpen,
  onClose,
  entries,
  onApplied,
  descriptor,
}: BulkEntityAIEnhanceProps<TEntry, TResult>) {
  const {
    collection: collectionName,
    formName,
    enrich,
    fieldOrder,
    suggestedKeys,
    fieldLabel,
    getExisting,
    getSuggestion,
    getConfidence,
    coerceValue,
    formatValue,
    buildPatch,
    alwaysApplyKeys,
    neverDefaultKeys,
    rowLabel,
    rowSubLabel,
    copy,
  } = descriptor;

  const [threshold, setThreshold] = useState<EnrichThreshold>('high');
  const [writeMode, setWriteMode] = useState<EnrichWriteMode>('fill');

  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState<Array<EntryRun<TEntry, TResult>>>([]);
  const [checked, setChecked] = useState<CheckedMap>({});
  const [applyStatus, setApplyStatus] = useState<Record<string, ApplyStatus>>({});
  const [applying, setApplying] = useState(false);
  const [doneRunning, setDoneRunning] = useState<
    { ok: number; failed: number; stopped: boolean } | null
  >(null);
  const stopRef = useRef(false);
  /**
   * Cells the reviewer ticked or unticked BY HAND, as `${entryId}::${fieldKey}`.
   *
   * Changing the threshold or the write mode re-seeds the checkbox map from the
   * new defaults — and used to do so over the top of individual decisions
   * already made. A never-default key was the worst case: tick the proposed
   * rename, then touch either radio, and it silently went back to unchecked, so
   * Apply wrote every other cell in the row and left the rename behind with no
   * indication anything had been dropped. The config controls decide DEFAULTS;
   * they must not overwrite a decision.
   *
   * Only individual cell clicks register here. The column header and the
   * Select all / none / high shortcuts are themselves re-seeding gestures, so
   * they stay subject to a later re-seed.
   */
  const touchedCells = useRef<Set<string>>(new Set());

  const hasResults = useMemo(() => runs.some((r) => r.status === 'done'), [runs]);

  // Default checkbox map for a set of results, given the current config.
  const computeDefaults = useCallback(
    (
      results: Array<EntryRun<TEntry, TResult>>,
      th: EnrichThreshold,
      wm: EnrichWriteMode,
    ): CheckedMap => {
      const next: CheckedMap = {};
      for (const r of results) {
        if (r.status !== 'done') continue;
        const row: Record<string, boolean> = {};
        for (const key of suggestedKeys(r.result, r.entry)) {
          if (neverDefaultKeys?.has(key)) {
            row[key] = false;
            continue;
          }
          const confidence = getConfidence(r.result, key);
          if (alwaysApplyKeys.has(key)) {
            // Checked regardless of write-mode or an existing value, but still
            // gated on the threshold — a low-confidence description starts
            // unchecked and can be ticked by hand.
            row[key] = meetsThreshold(confidence, th);
            continue;
          }
          const hasExisting = getExisting(r.entry, key).trim() !== '';
          row[key] = defaultChecked({
            confidence,
            hasExisting,
            threshold: th,
            writeMode: wm,
          });
        }
        next[r.entry.id] = row;
      }
      return next;
    },
    [suggestedKeys, neverDefaultKeys, getConfidence, alwaysApplyKeys, getExisting],
  );

  const updateRun = useCallback(
    (index: number, update: Partial<EntryRun<TEntry, TResult>>) => {
      setRuns((prev) => {
        const next = prev.slice();
        next[index] = { ...next[index], ...update };
        return next;
      });
    },
    [],
  );

  const handleRun = useCallback(async () => {
    if (entries.length === 0) return;
    stopRef.current = false;
    setRunning(true);
    setDoneRunning(null);
    setChecked({});
    setApplyStatus({});
    // New results, new decisions: nothing ticked against the old ones carries.
    touchedCells.current = new Set();

    // `collected` is the authoritative result array. Workers write into it by
    // index (single-threaded JS → safe between awaits) so after Promise.all the
    // defaults are computed without reading back from React state.
    const collected: Array<EntryRun<TEntry, TResult>> = entries.map((entry) => ({
      entry,
      status: 'queued' as RunStatus,
      result: {} as TResult,
    }));
    setRuns(collected.map((r) => ({ ...r })));

    let ok = 0;
    let failed = 0;
    let cursor = 0;

    const worker = async () => {
      while (true) {
        if (stopRef.current) return;
        const i = cursor;
        cursor += 1;
        if (i >= entries.length) return;

        const entry = entries[i];
        updateRun(i, { status: 'running' });
        try {
          const result = await enrich(entry);
          ok += 1;
          collected[i] = { ...collected[i], status: 'done', result };
          updateRun(i, { status: 'done', result });
        } catch (err) {
          failed += 1;
          const msg = err instanceof Error ? err.message : String(err);
          console.error(`Bulk AI enhance failed for ${rowLabel(entry)}:`, err);
          collected[i] = { ...collected[i], status: 'failed', error: msg };
          updateRun(i, { status: 'failed', error: msg });
        }
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    const stopped = stopRef.current;
    setRunning(false);
    setDoneRunning({ ok, failed, stopped });
    setChecked(computeDefaults(collected, threshold, writeMode));

    if (stopped) toast(`Stopped — ${ok} enhanced, ${failed} failed`);
    else if (failed === 0) toast.success(copy.runComplete(ok));
    else toast(`Done — ${ok} enhanced, ${failed} failed`);
  }, [entries, updateRun, computeDefaults, threshold, writeMode, enrich, rowLabel, copy]);

  const handleStop = useCallback(() => {
    stopRef.current = true;
  }, []);

  /**
   * Re-seed the defaults after a threshold / write-mode change, carrying every
   * hand-ticked cell across unchanged (see `touchedCells`). Reads the current
   * map through the state updater rather than the closure, so two config
   * changes in a row can't resurrect a stale selection.
   */
  const reseedDefaults = useCallback(
    (th: EnrichThreshold, wm: EnrichWriteMode) => {
      setChecked((prev) => {
        const next = computeDefaults(runs, th, wm);
        for (const touched of touchedCells.current) {
          const separator = touched.lastIndexOf('::');
          const entryId = touched.slice(0, separator);
          const key = touched.slice(separator + 2);
          if (next[entryId] && key in next[entryId]) {
            next[entryId][key] = !!prev[entryId]?.[key];
          }
        }
        return next;
      });
    },
    [computeDefaults, runs],
  );

  // Re-seed defaults when the admin changes threshold / write-mode AFTER a run.
  const onThresholdChange = (t: EnrichThreshold) => {
    setThreshold(t);
    if (hasResults) reseedDefaults(t, writeMode);
  };
  const onWriteModeChange = (m: EnrichWriteMode) => {
    setWriteMode(m);
    if (hasResults) reseedDefaults(threshold, m);
  };

  const doneRuns = useMemo(() => runs.filter((r) => r.status === 'done'), [runs]);

  // Columns = union of proposed keys across all done rows, in registry order.
  const columns = useMemo(() => {
    const present = new Set<string>();
    for (const r of doneRuns) for (const key of suggestedKeys(r.result, r.entry)) present.add(key);
    return fieldOrder.filter((k) => present.has(k));
  }, [doneRuns, suggestedKeys, fieldOrder]);

  const checkedCount = useMemo(() => {
    let n = 0;
    for (const row of Object.values(checked)) for (const v of Object.values(row)) if (v) n += 1;
    return n;
  }, [checked]);

  const setCell = useCallback((entryId: string, key: string, value: boolean) => {
    touchedCells.current.add(`${entryId}::${key}`);
    setChecked((prev) => ({
      ...prev,
      [entryId]: { ...(prev[entryId] || {}), [key]: value },
    }));
  }, []);

  // Column header select-all: toggle every cell in one column that has a
  // proposed value, leaving other columns untouched. Mode-agnostic — the user's
  // explicit "I want all of these", independent of writeMode / amber defaults.
  const setColumn = useCallback(
    (key: string, value: boolean) => {
      setChecked((prev) => {
        const next: CheckedMap = { ...prev };
        for (const r of doneRuns) {
          if (!suggestedKeys(r.result, r.entry).includes(key)) continue;
          next[r.entry.id] = { ...(next[r.entry.id] || {}), [key]: value };
        }
        return next;
      });
    },
    [doneRuns, suggestedKeys],
  );

  // Per-column tally of proposed cells vs currently-checked ones, so the header
  // checkbox can render checked / unchecked / indeterminate.
  const columnState = useMemo(() => {
    const state: Record<string, { total: number; checkedCount: number }> = {};
    for (const key of columns) {
      let total = 0;
      let checkedInCol = 0;
      for (const r of doneRuns) {
        if (!suggestedKeys(r.result, r.entry).includes(key)) continue;
        total += 1;
        if (checked[r.entry.id]?.[key]) checkedInCol += 1;
      }
      state[key] = { total, checkedCount: checkedInCol };
    }
    return state;
  }, [columns, doneRuns, checked, suggestedKeys]);

  // Toolbar shortcuts operate over every (entry, field) cell that has a suggestion.
  const bulkSet = useCallback(
    (predicate: (run: EntryRun<TEntry, TResult>, key: string) => boolean) => {
      setChecked(() => {
        const next: CheckedMap = {};
        for (const r of doneRuns) {
          const row: Record<string, boolean> = {};
          for (const key of suggestedKeys(r.result, r.entry)) row[key] = predicate(r, key);
          next[r.entry.id] = row;
        }
        return next;
      });
    },
    [doneRuns, suggestedKeys],
  );

  const selectAll = useCallback(() => bulkSet(() => true), [bulkSet]);
  const selectNone = useCallback(() => bulkSet(() => false), [bulkSet]);
  const selectAllHigh = useCallback(
    () => bulkSet((r, key) => getConfidence(r.result, key) === 'high'),
    [bulkSet, getConfidence],
  );
  const selectAllHighMedium = useCallback(
    () =>
      bulkSet((r, key) => {
        const c = getConfidence(r.result, key);
        return c === 'high' || c === 'medium';
      }),
    [bulkSet, getConfidence],
  );

  const handleApply = useCallback(async () => {
    setApplying(true);
    const status: Record<string, ApplyStatus> = {};
    let appliedRows = 0;
    let fieldCount = 0;
    let failedRows = 0;

    for (const r of doneRuns) {
      const row = checked[r.entry.id] || {};
      const acceptedKeys = Object.entries(row)
        .filter(([, on]) => on)
        .map(([key]) => key)
        /*
         * A ticked cell that AGREES with the document writes nothing new. In
         * overwrite mode those start checked, so applying would rewrite an
         * identical value — bumping `updatedAt` and re-triggering the Algolia
         * indexer for every row the model merely confirmed. Dropping them here
         * keeps "Apply N fields" honest about what actually changes.
         */
        .filter(
          (key) =>
            cellAgreement(
              getExisting(r.entry, key),
              formatValue(coerceValue(key, getSuggestion(r.result, key))),
            ) !== 'matches',
        );
      const patch = buildPatch(r.entry, acceptedKeys, r.result);
      const cells = Object.keys(patch).length;
      if (cells === 0) {
        status[r.entry.id] = 'skipped';
        continue;
      }
      patch.updatedAt = serverTimestamp();
      status[r.entry.id] = 'applying';
      setApplyStatus({ ...status });
      try {
        await updateDoc(doc(db, collectionName, r.entry.id), patch);
        status[r.entry.id] = 'applied';
        appliedRows += 1;
        // Count the cells that actually produced a patch key, not everything
        // ticked — an unusable value is dropped by buildPatch.
        fieldCount += cells;
      } catch (err) {
        console.error(`Apply failed for ${rowLabel(r.entry)}:`, err);
        status[r.entry.id] = 'failed';
        failedRows += 1;
      }
      setApplyStatus({ ...status });
    }

    setApplying(false);
    if (failedRows === 0) toast.success(copy.applied(fieldCount, appliedRows));
    else toast.error(copy.appliedWithFailures(appliedRows, failedRows));
    onApplied();
  }, [
    doneRuns,
    checked,
    onApplied,
    getExisting,
    formatValue,
    coerceValue,
    getSuggestion,
    buildPatch,
    collectionName,
    rowLabel,
    copy,
  ]);

  const processedCount = useMemo(
    () => runs.filter((r) => r.status === 'done' || r.status === 'failed').length,
    [runs],
  );

  const busy = running || applying;
  const failedRuns = runs.filter((r) => r.status === 'failed');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!busy) onClose();
      }}
      title={copy.title(entries.length)}
      size="full"
    >
      <div className="space-y-5">
        {/* Config */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Confidence threshold
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={`${formName}Threshold`}
                  checked={threshold === 'high'}
                  disabled={busy}
                  onChange={() => onThresholdChange('high')}
                />
                <span>High only</span>
                <span className="text-xs text-gray-400">(default)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={`${formName}Threshold`}
                  checked={threshold === 'highMedium'}
                  disabled={busy}
                  onChange={() => onThresholdChange('highMedium')}
                />
                <span>High + Medium</span>
              </label>
            </fieldset>

            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Write-back mode
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={`${formName}WriteMode`}
                  checked={writeMode === 'fill'}
                  disabled={busy}
                  onChange={() => onWriteModeChange('fill')}
                />
                <span>Fill empty fields only</span>
                <span className="text-xs text-gray-400">(default)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={`${formName}WriteMode`}
                  checked={writeMode === 'overwrite'}
                  disabled={busy}
                  onChange={() => onWriteModeChange('overwrite')}
                />
                <span>Overwrite existing values</span>
              </label>
            </fieldset>
          </div>
          <p className="text-xs text-gray-500">{copy.configNote}</p>

          <div className="flex items-center gap-2">
            {!running && !hasResults && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleRun}
                disabled={entries.length === 0}
              >
                {copy.runButton(entries.length)}
              </Button>
            )}
            {!running && hasResults && (
              <Button variant="secondary" size="sm" onClick={handleRun} disabled={applying}>
                Re-run
              </Button>
            )}
            {running && (
              <Button variant="danger" size="sm" onClick={handleStop}>
                Stop
              </Button>
            )}
            {running && (
              <span className="text-xs text-gray-500">
                Processing {processedCount} of {runs.length}…
              </span>
            )}
          </div>

          {running && runs.length > 0 && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-1.5 rounded-full bg-indigo-600 transition-all"
                style={{ width: `${(processedCount / runs.length) * 100}%` }}
              />
            </div>
          )}

          {doneRunning && (
            <div
              className={`rounded-md px-3 py-2 text-sm ${
                doneRunning.failed === 0 && !doneRunning.stopped
                  ? 'bg-green-50 text-green-800'
                  : 'bg-amber-50 text-amber-800'
              }`}
            >
              {doneRunning.stopped ? 'Stopped. ' : 'Enhancement complete. '}
              {doneRunning.ok} enhanced, {doneRunning.failed} failed.
            </div>
          )}
        </div>

        {/* Review grid */}
        {hasResults && columns.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Select:</span>
                <button
                  type="button"
                  className="text-indigo-600 hover:underline disabled:text-gray-300"
                  onClick={selectAll}
                  disabled={applying}
                >
                  All
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  className="text-indigo-600 hover:underline disabled:text-gray-300"
                  onClick={selectNone}
                  disabled={applying}
                >
                  None
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  className="text-indigo-600 hover:underline disabled:text-gray-300"
                  onClick={selectAllHigh}
                  disabled={applying}
                >
                  All high-confidence
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  className="text-indigo-600 hover:underline disabled:text-gray-300"
                  onClick={selectAllHighMedium}
                  disabled={applying}
                >
                  All high + medium
                </button>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleApply}
                disabled={applying || checkedCount === 0}
                loading={applying}
              >
                Apply {checkedCount} field{checkedCount === 1 ? '' : 's'}
              </Button>
            </div>

            <div className="overflow-auto border border-gray-200 rounded-md max-h-[28rem]">
              <table className="text-xs border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    <th className="sticky left-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">
                      {copy.rowHeader}
                    </th>
                    {columns.map((key) => {
                      const col = columnState[key] ?? { total: 0, checkedCount: 0 };
                      return (
                        <th
                          key={key}
                          className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                        >
                          <span className="flex items-center gap-1.5">
                            <ColumnSelectAllCheckbox
                              total={col.total}
                              checkedCount={col.checkedCount}
                              onToggle={(value) => setColumn(key, value)}
                              disabled={applying}
                              label={fieldLabel(key)}
                            />
                            {fieldLabel(key)}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {doneRuns.map((r) => {
                    const aStatus = applyStatus[r.entry.id] || 'idle';
                    const proposed = suggestedKeys(r.result, r.entry);
                    const sub = rowSubLabel?.(r.entry);
                    return (
                      <tr key={r.entry.id} className="align-top">
                        <th className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-2 py-1.5 text-left font-mono font-normal text-gray-700 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <ConfidenceDot
                              level={bestConfidence(r.result, proposed, getConfidence)}
                            />
                            <span>{rowLabel(r.entry)}</span>
                            <ApplyDot status={aStatus} />
                          </div>
                          {sub && (
                            <div className="font-sans text-[10px] text-gray-400 truncate max-w-[10rem]">
                              {sub}
                            </div>
                          )}
                        </th>
                        {columns.map((key) => {
                          if (!proposed.includes(key)) {
                            return (
                              <td
                                key={key}
                                className="border-b border-gray-100 px-2 py-1.5 text-gray-300"
                              >
                                —
                              </td>
                            );
                          }
                          const existing = getExisting(r.entry, key);
                          const isChecked = !!checked[r.entry.id]?.[key];
                          const value = coerceValue(key, getSuggestion(r.result, key));
                          const confidence = getConfidence(r.result, key);
                          /*
                           * Three states, not two. A proposal that AGREES with
                           * the document used to look exactly like one that
                           * contradicts it — and the distinction is the whole
                           * point of the column.
                           */
                          const agreement = cellAgreement(existing, formatValue(value));
                          return (
                            <td
                              key={key}
                              className={`border-b border-gray-100 px-2 py-1.5 ${
                                agreement === 'differs'
                                  ? 'bg-amber-50'
                                  : agreement === 'matches'
                                    ? 'bg-emerald-50/60'
                                    : ''
                              }`}
                            >
                              <label className="flex items-start gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 shrink-0"
                                  checked={isChecked}
                                  disabled={applying}
                                  onChange={(e) => setCell(r.entry.id, key, e.target.checked)}
                                />
                                <span className="min-w-0 max-w-[16rem]">
                                  <span className="flex items-center gap-1">
                                    <ConfidenceDot level={confidence} />
                                    {formatValue(value) ? (
                                      <ExpandableText
                                        text={formatValue(value)}
                                        className="text-gray-800 break-words"
                                      />
                                    ) : (
                                      <span className="text-gray-300">∅</span>
                                    )}
                                  </span>
                                  {agreement === 'matches' && (
                                    <span className="block mt-0.5 text-[10px] text-emerald-700">
                                      ✓ matches stored value
                                    </span>
                                  )}
                                  {agreement === 'differs' && (
                                    <span className="block mt-0.5">
                                      <ExpandableText
                                        text={`existing: ${existing}`}
                                        className="text-[10px] text-amber-700 break-words"
                                      />
                                    </span>
                                  )}
                                </span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-gray-400">
              <span className="inline-block w-2 h-2 rounded-sm bg-emerald-50 border border-emerald-200 align-middle" />{' '}
              agrees with the stored value (nothing to write) ·{' '}
              <span className="inline-block w-2 h-2 rounded-sm bg-amber-50 border border-amber-200 align-middle" />{' '}
              differs from the stored value ·{' '}
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 align-middle" />{' '}
              high ·{' '}
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 align-middle" />{' '}
              medium ·{' '}
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 align-middle" />{' '}
              low. {copy.legendNote}
            </p>
          </div>
        )}

        {/* Failures */}
        {failedRuns.length > 0 && (
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer">{copy.failures(failedRuns.length)}</summary>
            <ul className="mt-1 space-y-0.5">
              {failedRuns.map((r) => (
                <li key={r.entry.id} className="font-mono">
                  {rowLabel(r.entry)}
                  <span className="ml-2 font-sans text-red-600">
                    {r.error ? r.error.slice(0, 80) : 'Failed'}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Best per-field confidence in a result — the row-level summary dot. */
function bestConfidence<TResult>(
  result: TResult,
  keys: string[],
  getConfidence: (result: TResult, key: string) => string,
): string {
  let best = 'low';
  for (const key of keys) {
    const c = getConfidence(result, key);
    if (c === 'high') return 'high';
    if (c === 'medium') best = 'medium';
  }
  return best;
}

// Two-line clamp applied via inline style, not the Tailwind `line-clamp-2`
// utility. The clamp needs `display: -webkit-box`, but any co-applied `block`
// utility emits `display: block` LATER in the stylesheet and, at equal
// specificity, wins — silently disabling the clamp so the cell renders full
// height. Inline styles beat any class regardless of emission order.
const CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

/**
 * Renders potentially-long cell text clamped to 2 lines, with an inline
 * Show more / Show less toggle, so the grid stays scannable. Purely visual —
 * the underlying value is never altered.
 */
function ExpandableText({ text, className = '' }: { text: string; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // Measure only while clamped — expanded, scrollHeight === clientHeight, so
    // re-measuring would wrongly clear `overflowing` and hide the toggle.
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  return (
    <span className="block">
      <span
        ref={ref}
        className={`block ${className}`}
        style={expanded ? undefined : CLAMP_STYLE}
        title={text}
      >
        {text}
      </span>
      {(overflowing || expanded) && (
        <button
          type="button"
          className="mt-0.5 text-[10px] font-medium text-indigo-600 hover:underline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </span>
  );
}

function ConfidenceDot({ level }: { level: string }) {
  const color =
    level === 'high' ? 'bg-green-500' : level === 'medium' ? 'bg-amber-400' : 'bg-gray-300';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`}
      title={`${level} confidence`}
    />
  );
}

function ApplyDot({ status }: { status: ApplyStatus }) {
  switch (status) {
    case 'applying':
      return <span className="text-blue-600 text-[10px]">…</span>;
    case 'applied':
      return (
        <span className="text-green-600 text-[10px]" title="Applied">
          ✓
        </span>
      );
    case 'failed':
      return (
        <span className="text-red-600 text-[10px]" title="Apply failed">
          ✗
        </span>
      );
    case 'skipped':
      return (
        <span className="text-gray-300 text-[10px]" title="Nothing checked">
          –
        </span>
      );
    default:
      return null;
  }
}
